
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { CompanyRequest } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { getStandardizedLocation } from '@/utils/locationHelpers';

export const useCompanyRequestStore = create<{
  requests: CompanyRequest[];
  addRequest: (requestData: Partial<CompanyRequest>) => Promise<CompanyRequest | null>;
  updateRequestStatus: (id: string, status: 'pending' | 'approved' | 'rejected') => Promise<boolean>;
  fetchRequests: () => Promise<CompanyRequest[]>;
}>((set, get) => ({
  requests: [],
  
  // Save company requests to Supabase - now anyone can submit due to RLS policy
  addRequest: async (requestData) => {
    try {
      const standardizedLocation = getStandardizedLocation(requestData.location || '');
      
      const newRequest = {
        id: uuidv4(),
        status: 'pending',
        createdAt: Date.now(),
        ...requestData,
        location: standardizedLocation
      } as CompanyRequest;
      
      // Save to Supabase
      const { data, error } = await supabase
        .from('company_requests')
        .insert({
          id: newRequest.id,
          company_name: newRequest.companyName,
          location: newRequest.location,
          contact_email: newRequest.contactEmail,
          additional_info: newRequest.additionalInfo || '',
          status: newRequest.status,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
        
      if (error) {
        console.error("Error saving company request to Supabase:", error);
        throw new Error(`Failed to save company request: ${error.message}`);
      }
      
      // Update local cache
      set((state) => ({
        requests: [...state.requests, newRequest]
      }));
      
      return newRequest;
    } catch (error) {
      console.error("Error in addRequest:", error);
      return null;
    }
  },
  
  // Update request status - now requires admin privileges due to RLS
  updateRequestStatus: async (id, status) => {
    try {
      // Check if user is authenticated and has admin privileges
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required to update request status');
      }
      
      if (!user.email?.endsWith('@redflags.au')) {
        throw new Error('Admin privileges required to update request status');
      }
      
      // Update in Supabase
      const { error } = await supabase
        .from('company_requests')
        .update({ status })
        .eq('id', id);
        
      if (error) {
        console.error("Error updating request status in Supabase:", error);
        throw new Error(`Failed to update request status: ${error.message}`);
      }
      
      // Update local state
      set((state) => ({
        requests: state.requests.map((request) => 
          request.id === id ? { ...request, status } : request
        )
      }));
      
      return true;
    } catch (error) {
      console.error("Error in updateRequestStatus:", error);
      return false;
    }
  },
  
  // Fetch company requests - now only accessible to admins due to RLS
  fetchRequests: async () => {
    try {
      const { data, error } = await supabase
        .from('company_requests')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching company requests from Supabase:", error);
        // Return cached data if user doesn't have permission or other error
        return get().requests;
      }
      
      // Convert Supabase data to our app's CompanyRequest format
      const formattedRequests: CompanyRequest[] = (data || []).map(request => ({
        id: request.id,
        companyName: request.company_name,
        location: request.location,
        contactEmail: request.contact_email,
        additionalInfo: request.additional_info || '',
        status: request.status as 'pending' | 'approved' | 'rejected',
        createdAt: new Date(request.created_at).getTime()
      }));
      
      // Update local cache
      set({ requests: formattedRequests });
      
      return formattedRequests;
    } catch (error) {
      console.error("Error in fetchRequests:", error);
      return get().requests;
    }
  }
}));
